// src/components/ConfirmSave.jsx
// Shows a review popup before any entry is saved.
// Used in Purchase, Production, Transfer, Sales, CakeSale.

import { fmt, fmtL, fmtKg, OilDot, PayPill } from "../utils/ui";

const overlay = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.55)",
  zIndex: 300,
  display: "flex", alignItems: "flex-end", justifyContent: "center",
};

const box = {
  background: "#fff",
  borderRadius: "16px 16px 0 0",
  padding: "20px 18px 36px",
  width: "100%", maxWidth: 430,
};

const title = {
  fontSize: 16, fontWeight: 700,
  color: "#1a1a1a", marginBottom: 4,
};

const subtitle = {
  fontSize: 12, color: "#888", marginBottom: 16,
};

const row = {
  display: "flex", justifyContent: "space-between",
  alignItems: "center", padding: "8px 0",
  borderBottom: "1px solid #F0EDE6",
};

const rowLast = {
  display: "flex", justifyContent: "space-between",
  alignItems: "center", padding: "8px 0",
};

const label = { fontSize: 13, color: "#666" };
const value = { fontSize: 13, fontWeight: 600, color: "#1a1a1a" };

const btnRow = {
  display: "flex", gap: 10, marginTop: 20,
};

const cancelBtn = {
  flex: 1, padding: "12px", borderRadius: 8,
  background: "#F7F4EE", color: "#555",
  border: "1px solid #E0DDD5", fontSize: 14,
  fontWeight: 600, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

const confirmBtn = {
  flex: 2, padding: "12px", borderRadius: 8,
  background: "#27500A", color: "#C0DD97",
  border: "none", fontSize: 14,
  fontWeight: 600, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

const confirmBtnSaving = {
  ...confirmBtn,
  background: "#888", cursor: "not-allowed",
};

// ── PURCHASE CONFIRM ─────────────────────────────────────────────────────────
export function ConfirmPurchase({ data, onConfirm, onCancel, saving }) {
  return (
    <div style={overlay}>
      <div style={box}>
        <div style={title}>Confirm Purchase</div>
        <div style={subtitle}>Review before saving to Google Sheets</div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "12px 14px", marginBottom: 4 }}>
          <div style={row}>
            <span style={label}>Oil seed</span>
            <span style={{ ...value, display: "flex", alignItems: "center" }}>
              <OilDot oil={data.oil} />{data.oil}
            </span>
          </div>
          <div style={row}>
            <span style={label}>Quantity</span>
            <span style={value}>{data.kgs} kg</span>
          </div>
          <div style={row}>
            <span style={label}>Price per kg</span>
            <span style={value}>₹{data.price}/kg</span>
          </div>
          {data.supplier && (
            <div style={row}>
              <span style={label}>Supplier</span>
              <span style={value}>{data.supplier}</span>
            </div>
          )}
          <div style={row}>
            <span style={label}>Date</span>
            <span style={value}>{data.date}</span>
          </div>
          <div style={rowLast}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Total value</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#27500A" }}>{fmt(data.kgs * data.price)}</span>
          </div>
        </div>
        <div style={btnRow}>
          <button style={cancelBtn} onClick={onCancel}>Edit</button>
          <button style={saving ? confirmBtnSaving : confirmBtn} onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : "✓ Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PRODUCTION CONFIRM ───────────────────────────────────────────────────────
export function ConfirmBatch({ data, onConfirm, onCancel, saving }) {
  return (
    <div style={overlay}>
      <div style={box}>
        <div style={title}>Confirm Production Batch</div>
        <div style={subtitle}>Review before saving to Google Sheets</div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "12px 14px", marginBottom: 4 }}>
          <div style={row}>
            <span style={label}>Oil seed</span>
            <span style={{ ...value, display: "flex", alignItems: "center" }}>
              <OilDot oil={data.oil} />{data.oil}
            </span>
          </div>
          <div style={row}>
            <span style={label}>Raw material used</span>
            <span style={value}>{fmtKg(data.rawUsed)}</span>
          </div>
          <div style={row}>
            <span style={label}>Oil produced</span>
            <span style={{ ...value, color: "#27500A" }}>{fmtL(data.oilLitres)}</span>
          </div>
          <div style={row}>
            <span style={label}>Cake produced</span>
            <span style={{ ...value, color: "#185FA5" }}>{fmtKg(data.cakeKg)}</span>
          </div>
          <div style={row}>
            <span style={label}>Yield</span>
            <span style={value}>{data.yieldOil}% oil · {data.yieldCake}% cake</span>
          </div>
          <div style={rowLast}>
            <span style={label}>Date</span>
            <span style={value}>{data.date}</span>
          </div>
        </div>
        <div style={btnRow}>
          <button style={cancelBtn} onClick={onCancel}>Edit</button>
          <button style={saving ? confirmBtnSaving : confirmBtn} onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : "✓ Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TRANSFER CONFIRM ─────────────────────────────────────────────────────────
export function ConfirmTransfer({ data, onConfirm, onCancel, saving }) {
  return (
    <div style={overlay}>
      <div style={box}>
        <div style={title}>Confirm Transfer</div>
        <div style={subtitle}>Plant → Shop · Review before saving</div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "12px 14px", marginBottom: 4 }}>
          <div style={row}>
            <span style={label}>Oil type</span>
            <span style={{ ...value, display: "flex", alignItems: "center" }}>
              <OilDot oil={data.oil} />{data.oil}
            </span>
          </div>
          <div style={row}>
            <span style={label}>Pack size</span>
            <span style={value}>{data.sku}</span>
          </div>
          <div style={row}>
            <span style={label}>Units</span>
            <span style={value}>{data.count} bottles/cans</span>
          </div>
          <div style={row}>
            <span style={label}>Volume</span>
            <span style={{ ...value, color: "#27500A" }}>{fmtL(data.litres)}</span>
          </div>
          <div style={row}>
            <span style={label}>Date</span>
            <span style={value}>{data.date}</span>
          </div>
          <div style={rowLast}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Est. value</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#27500A" }}>{fmt(data.value)}</span>
          </div>
        </div>
        <div style={btnRow}>
          <button style={cancelBtn} onClick={onCancel}>Edit</button>
          <button style={saving ? confirmBtnSaving : confirmBtn} onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : "✓ Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SALE CONFIRM ─────────────────────────────────────────────────────────────
export function ConfirmSale({ data, onConfirm, onCancel, saving }) {
  return (
    <div style={overlay}>
      <div style={box}>
        <div style={title}>Confirm Sale</div>
        <div style={subtitle}>Review before saving to Google Sheets</div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "12px 14px", marginBottom: 4 }}>
          <div style={row}>
            <span style={label}>Oil type</span>
            <span style={{ ...value, display: "flex", alignItems: "center" }}>
              <OilDot oil={data.oil} />{data.oil}
            </span>
          </div>
          <div style={row}>
            <span style={label}>Pack size</span>
            <span style={value}>{data.sku}</span>
          </div>
          <div style={row}>
            <span style={label}>Quantity</span>
            <span style={value}>{data.qty} units ({fmtL(data.litres)})</span>
          </div>
          <div style={row}>
            <span style={label}>Location</span>
            <span style={value}>{data.location}</span>
          </div>
          <div style={row}>
            <span style={label}>Payment</span>
            <span style={value}><PayPill mode={data.mode} /> {data.mode}</span>
          </div>
          {data.buyerName && (
            <div style={row}>
              <span style={label}>Buyer</span>
              <span style={value}>{data.buyerName}</span>
            </div>
          )}
          {data.buyerPhone && (
            <div style={row}>
              <span style={label}>Phone</span>
              <span style={value}>{data.buyerPhone}</span>
            </div>
          )}
          <div style={row}>
            <span style={label}>Date</span>
            <span style={value}>{data.date}</span>
          </div>
          <div style={rowLast}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Amount</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#27500A" }}>{fmt(data.amount)}</span>
          </div>
        </div>
        <div style={btnRow}>
          <button style={cancelBtn} onClick={onCancel}>Edit</button>
          <button style={saving ? confirmBtnSaving : confirmBtn} onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : "✓ Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CAKE SALE CONFIRM ────────────────────────────────────────────────────────
export function ConfirmCakeSale({ data, onConfirm, onCancel, saving }) {
  return (
    <div style={overlay}>
      <div style={box}>
        <div style={title}>Confirm Cake Sale</div>
        <div style={subtitle}>Review before saving to Google Sheets</div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "12px 14px", marginBottom: 4 }}>
          <div style={row}>
            <span style={label}>Oil type</span>
            <span style={{ ...value, display: "flex", alignItems: "center" }}>
              <OilDot oil={data.oil} />{data.oil} cake
            </span>
          </div>
          <div style={row}>
            <span style={label}>Quantity</span>
            <span style={value}>{fmtKg(data.kg)}</span>
          </div>
          <div style={row}>
            <span style={label}>Price per kg</span>
            <span style={value}>₹{(data.amount / data.kg).toFixed(0)}/kg</span>
          </div>
          {data.buyerName && (
            <div style={row}>
              <span style={label}>Buyer</span>
              <span style={value}>{data.buyerName}</span>
            </div>
          )}
          {data.buyerPhone && (
            <div style={row}>
              <span style={label}>Phone</span>
              <span style={value}>{data.buyerPhone}</span>
            </div>
          )}
          <div style={row}>
            <span style={label}>Payment</span>
            <span style={value}><PayPill mode={data.mode} /> {data.mode}</span>
          </div>
          <div style={row}>
            <span style={label}>Date</span>
            <span style={value}>{data.date}</span>
          </div>
          <div style={rowLast}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>Amount</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#27500A" }}>{fmt(data.amount)}</span>
          </div>
        </div>
        <div style={btnRow}>
          <button style={cancelBtn} onClick={onCancel}>Edit</button>
          <button style={saving ? confirmBtnSaving : confirmBtn} onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : "✓ Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
